import React, { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import BackOfficeLayout from "../../../../components/backoffice-layout";
import LoadingModal from "../../../../components/loading-modal";
import CoreAPI from "../../../../services/core";
import { useOpenModalTokenExpired } from "../../../../contexts";
import FilterBar from "./components/FilterBar";
import KpiTiles from "./components/KpiTiles";
import TimeseriesChart from "./components/TimeseriesChart";
import BreakdownChart from "./components/BreakdownChart";
import TopListTable from "./components/TopListTable";
import DetailModal from "./components/DetailModal";

const Container = styled.div`
    width: calc(100% - 32px);
    background-color: #FFFFFF;
    padding: 24px 16px;
    border-radius: 16px;

    .h1 { color: #25368D; font-size: 20px; font-weight: 600; margin-bottom: 16px; }
    .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px;
              @media (max-width: 1024px) { grid-template-columns: 1fr; } }
`;

function defaultFilter() {
    const now = dayjs();
    return {
        start_date: now.startOf("month").toISOString(),
        end_date:   now.endOf("day").toISOString(),
        period: "thisMonth",
        evaluation_type: null, division_id: null, evaluation_form_id: null,
        location_id: null, province: null,
    };
}

function buildQueryParam(filter) {
    const out = { start_date: filter.start_date, end_date: filter.end_date };
    if (filter.evaluation_type)    out.evaluation_type = filter.evaluation_type;
    if (filter.division_id != null) out.division_id     = filter.division_id;
    if (filter.evaluation_form_id) out.evaluation_form_id = filter.evaluation_form_id;
    if (filter.location_id != null) out.location_id     = filter.location_id;
    if (filter.province)           out.province         = filter.province;
    return out;
}

export default function DashboardUsagePage() {
    const { setOpenModalTokenExpired } = useOpenModalTokenExpired();
    const [filter, setFilter] = useState(defaultFilter);
    const [data, setData]     = useState(null);
    const [loading, setLoading] = useState(false);
    const [modal, setModal]     = useState({ open: false, entityType: null, entityId: null });

    useEffect(() => {
        CoreAPI.dashboardHttpService?.setTokenExpiredHandler?.(setOpenModalTokenExpired);
        onApply();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function onApply() {
        setLoading(true);
        try {
            const res = await CoreAPI.dashboardHttpService.getUsage(buildQueryParam(filter));
            setData(res);
        } catch { } finally { setLoading(false); }
    }

    function onReset() { setFilter(defaultFilter()); }

    async function onExport() {
        try {
            const res = await CoreAPI.dashboardHttpService.exportUsage(buildQueryParam(filter));
            const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cd = res.headers?.["content-disposition"] || "";
            const m = /filename="([^"]+)"/.exec(cd);
            a.download = m ? m[1] : `sat-dashboard-usage.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch { }
    }

    function openEvaluator(row) { setModal({ open: true, entityType: "evaluator", entityId: row.evaluator_id }); }
    function openForm(row)      { setModal({ open: true, entityType: "form",      entityId: row.evaluation_form_id }); }
    function openLocation(row)  { setModal({ open: true, entityType: "location",  entityId: String(row.location_id) }); }
    function closeModal()       { setModal({ open: false, entityType: null, entityId: null }); }

    return (
        <BackOfficeLayout>
            <LoadingModal open={loading} />
            <Container>
                <FilterBar filter={filter} setFilter={setFilter} onApply={onApply} onReset={onReset} onExport={onExport} isLoading={loading} />
                <KpiTiles summary={data?.summary} />
                <TimeseriesChart data={data?.timeseries} />
                <div className="grid-3">
                    <BreakdownChart title="แยกตามประเภท" data={data?.breakdown_by_type}     labelKey="evaluation_type" />
                    <BreakdownChart title="แยกตามฝ่าย"   data={data?.breakdown_by_division} labelKey="division_name" />
                    <BreakdownChart title="แยกตามจังหวัด" data={data?.breakdown_by_province} labelKey="province" />
                </div>
                <div className="grid-3">
                    <TopListTable
                        title="แบบสอบถามยอดนิยม"
                        data={data?.top_forms}
                        columns={[
                            { key: "evaluation_name", label: "แบบสอบถาม" },
                            { key: "evaluation_type", label: "ประเภท" },
                            { key: "submissions",     label: "Submissions", type: "num" },
                        ]}
                        onRowClick={openForm}
                    />
                    <TopListTable
                        title="สถานที่ยอดนิยม"
                        data={data?.top_locations}
                        columns={[
                            { key: "location_name", label: "สถานที่" },
                            { key: "attendances",   label: "Attendances", type: "num" },
                        ]}
                        onRowClick={openLocation}
                    />
                    <TopListTable
                        title="ผู้ใช้งานยอดนิยม"
                        data={data?.top_evaluators}
                        columns={[
                            { key: "evaluator_name", label: "ผู้ใช้งาน" },
                            { key: "submissions",    label: "Subs", type: "num" },
                            { key: "attendances",    label: "Atts", type: "num" },
                            { key: "total",          label: "Total", type: "num" },
                        ]}
                        onRowClick={openEvaluator}
                    />
                </div>
            </Container>
            <DetailModal
                open={modal.open}
                entityType={modal.entityType}
                entityId={modal.entityId}
                startDate={filter.start_date}
                endDate={filter.end_date}
                onClose={closeModal}
            />
        </BackOfficeLayout>
    );
}
